import React, { useEffect, useState, useRef } from 'react';
import { StyleSheet, Text, View, TouchableOpacity, ScrollView, Image, TextInput } from 'react-native';
import { Storage, DataStore } from 'aws-amplify';
import { Message, SendPicture, AppUser, UserGroup, Conversation } from '../../models';
import * as ImagePicker from 'expo-image-picker';
import * as CmnUtil from '../../components/CmnUtil';
import * as AppConst from '../../components/AppConst';
import IconImages from '../../components/IconImages';

class ChatContent {
  id: string = '';
  senderId: string = '';
  content: string = '';
  sendPhotokey: string = '';
  type: string = '';
  date: string = '';
  isRead: boolean = false;
}

/** ================================
*  チャット画面
*  ================================*/
const ChatMain = ({ route, navigation }: { route: any, navigation: any }) => {

  // チャット一覧
  const [chatContentList, setChatContentList] = useState<ChatContent[]>(Array());
  // 入力メッセージ
  const [inputValue, setInputValue] = useState('');
  // メッセージ表示部
  const scrollView = useRef<ScrollView>(null);
  // 引き継ぎパラメータ
  const conversasion: Conversation = route.params.groupConversation.conversation;
  const sender: AppUser = route.params.userInfo;
  const partner: UserGroup = route.params.partner;
  // 日付ラベル
  let lastDayLabel = '';
  // 画像拡大表示
  const [enlargementImage, setEnlargementImage] = useState<string | undefined>(undefined);

  useEffect(() => {
    // ヘッダの設定
    navigation.setOptions({ headerTitle: route.params.partner?.groupName });

    // チャット内容の読み込み
    fetchMessage();
  }, []);

  useEffect(() => {
    // メッセージの既読処理
    chatContentList.map((item: ChatContent) => {
      if (item.senderId !== sender.id && !item.isRead) {
        updateReadFlg(item);
      }
    });

    // チャット情報の購読
    const message = DataStore.observe(Message).subscribe(
      value => {
        const tmpValue: any = value;
        if (tmpValue.opType == 'INSERT' && tmpValue.element.conversationID == conversasion.id) {
          let content = new ChatContent();
          content.id = tmpValue.element.id;
          content.type = 'message';
          content.content = tmpValue.element.content;
          content.senderId = tmpValue.element.senderID;
          content.date = CmnUtil.getDate();
          content.isRead = tmpValue.element.isRead == true;
          setChatContentList([...chatContentList, content]);
        }
      }
    );
    const sendPicture = DataStore.observe(SendPicture).subscribe(
      value => {
        const tmpValue: any = value;
        if (tmpValue.opType == 'INSERT' && tmpValue.element.conversationID == conversasion.id) {
          let content = new ChatContent();
          content.id = tmpValue.element.id;
          content.type = 'photo';
          content.senderId = tmpValue.element.senderID;
          content.sendPhotokey = tmpValue.element.sendPhotokey;
          content.date = CmnUtil.getDate();
          content.isRead = tmpValue.element.isRead == true;
          setChatContentList([...chatContentList, content]);
        }
      }
    );

    // 最下部にスクロール
    scrollView?.current?.scrollToEnd({ animated: false });

    return () => {
      message.unsubscribe();
      sendPicture.unsubscribe();
    }
  }, [chatContentList]);

  // メッセージの取得
  const fetchMessage = async () => {
    let messageArray: ChatContent[] = [];

    if (conversasion.id) {
      // メッセージの取得
      const message_data = (await DataStore.query(Message)).filter(
        c => c.conversation.id === conversasion.id);
      message_data.map((message) => {
        let content = new ChatContent();
        content.id = message.id;
        content.type = 'message';
        content.content = message.content;
        content.senderId = message.sender.id;
        content.date = message.createdAt!;
        content.isRead = message.isRead == true;
        messageArray.push(content);
      });

      // 送信画像の取得
      const picture_data = (await DataStore.query(SendPicture)).filter(
        c => c.conversation.id === conversasion.id);
      picture_data.map((picture) => {
        let content = new ChatContent();
        content.id = picture.id;
        content.type = 'photo';
        content.senderId = picture.sender.id;
        content.date = picture.createdAt!;
        content.sendPhotokey = picture.sendPhotokey;
        content.isRead = picture.isRead == true;
        messageArray.push(content);
      });

      // メッセージを日付順に並べ替える
      messageArray.sort((a, b) => a.date > b.date ? 1 : -1);
      // メッセージ一覧を設定し、描画
      setChatContentList(messageArray);
    }
  };

  // 既読フラグの更新
  const updateReadFlg = async (item: ChatContent) => {
    if (item.type === 'message') {
      // メッセージの保存
      const original = await DataStore.query(Message, item.id);
      if (original != undefined) {
        await DataStore.save(
          Message.copyOf(original, updated => {
            updated.isRead = true;
          })
        );
      }
    } else {
      // 送信画像の保存
      const original = await DataStore.query(SendPicture, item.id);
      if (original != undefined) {
        await DataStore.save(
          SendPicture.copyOf(original, updated => {
            updated.isRead = true;
          })
        );
      }
    }
  };

  // メッセージの送信
  const sendMessage = async () => {
    if (inputValue !== '') {
      // メッセージの保存
      await DataStore.save(new Message({
        conversation: conversasion,
        sender: sender,
        content: inputValue,
        isSent: true,
        isRead: false,
      }));

      // テキストエリアのクリア
      setInputValue('');
    }
  };

  // 画像選択
  const selectImage = async () => {
    // 画像を選択するシステムUIを表示
    let result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowEditiong: false,
      aspect: [4, 3],
      quality: 1,
    });

    if (!result.cancelled) {
      sendImage(result);
    };
  };

  // 画像の送信
  const sendImage = async (pickerResult: any) => {
    try {
      // S3アップロード
      const upload_path = sender.id + '/chat/' + conversasion.id + '/';
      const file_name = CmnUtil.getCurrentDateTime();
      const img = await CmnUtil.blobConvert(pickerResult.uri);
      const ext = CmnUtil.getExt(pickerResult.uri);
      const uploadUrl = await Storage.put(upload_path + file_name + '.' + ext, img);

      // DataStore登録
      DataStore.save(new SendPicture({
        conversation: conversasion,
        sender: sender,
        sendPhotokey: uploadUrl.key,
        isSent: true,
        isRead: false,
      }));

    } catch (e) {
      console.log(e);
      CmnUtil.appAlert('画像の送信に失敗しました。');
    }
  };

  return (
    <View style={styles.container}>
      { /* メッセージ表示部 */}
      <View style={styles.messageContainer}>
        <ScrollView ref={scrollView}>
          {chatContentList && chatContentList.map((message, index) => {
            let dayLabel = CmnUtil.formatDateAndWeek(CmnUtil.formatToDate(message.date));
            if (lastDayLabel == dayLabel) {
              dayLabel = "";
            } else {
              lastDayLabel = dayLabel;
            }

            return (
              <View key={index}>
                {dayLabel != "" && <View style={styles.dayLabelArea}>
                  <Text style={styles.dayLabel}>{dayLabel}</Text>
                </View>}
                {message.senderId !== sender.id ?
                  message.type === 'message' ?
                    <View style={styles.fromThemBox}>
                      <SenderIcon senderId={message.senderId} />
                      <View style={styles.fromThem} >
                        <Text style={styles.chatText}>{message.content}</Text>
                        <View style={styles.leftArrow} />
                        <View style={styles.leftArrowOverlap} />
                      </View>
                    </View>
                    :
                    <View style={styles.fromThemBox}>
                      <SenderIcon senderId={message.senderId} />
                      <TouchableOpacity style={styles.fromThemPhoto} onPress={() => { setEnlargementImage(message.sendPhotokey) }}>
                        <CmnUtil.S3Image imageKey={message.sendPhotokey} style={styles.chatPhoto} />
                      </TouchableOpacity>
                    </View>
                  :
                  message.type === 'message' ?
                    <View style={styles.fromMe}>
                      <Text style={styles.chatText}>{message.content}</Text>
                      <View style={styles.rightArrow} />
                      <View style={styles.rightArrowOverlap} />
                    </View>
                    :
                    <TouchableOpacity style={styles.fromMePhoto} onPress={() => { setEnlargementImage(message.sendPhotokey) }}>
                      <CmnUtil.S3Image imageKey={message.sendPhotokey} style={styles.chatPhoto} />
                    </TouchableOpacity>
                }
              </View>
            );
          })
          }
        </ScrollView>
      </View>

      { /* 操作エリア部 */}
      <View>
        { /* メッセージ */}
        <View style={styles.formContainer}>
          <TouchableOpacity onPress={() => { setInputValue(''); }}>
            <Image style={styles.messageIcon} source={IconImages.ic_close} />
          </TouchableOpacity>
          <View style={styles.messageInputBase}>
            <Text style={{ opacity: 0, maxHeight: 90 }}>
              {inputValue};
            </Text>
            <TextInput
              style={styles.messageInput}
              value={inputValue}
              onChangeText={(text) => setInputValue(text)}
              multiline={true}
              placeholder='メッセージを入力してください'
            />
          </View>
          <TouchableOpacity onPress={() => { sendMessage() }}>
            <Image style={styles.messageIcon} source={IconImages.ic_send} />
          </TouchableOpacity>
        </View>

        { /* 画像 */}
        <View style={styles.imageContainer}>
          <TouchableOpacity onPress={selectImage}>
            <Image style={styles.pictureIcon} source={IconImages.ic_gallery} />
          </TouchableOpacity>
          <Text style={styles.iconText}>写真を送信</Text>
        </View>
      </View>

      { /* 画像拡大表示 */}
      {enlargementImage && <TouchableOpacity style={{ position: 'absolute', width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.4)' }} onPress={() => { setEnlargementImage(undefined) }}>
        <CmnUtil.S3Image imageKey={enlargementImage} style={{ width: '100%', height: '100%' }} />
      </TouchableOpacity>}
    </View>
  );
}

// 送信者アイコン
const SenderIcon = ({ senderId }: { senderId: string }) => {

  const [icon, setIcon] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    (async () => {
      const sender = await DataStore.query(AppUser, senderId);
      setIcon(sender?.icon);
    })();
  }, [senderId]);

  return (
    <CmnUtil.S3Image imageKey={icon} style={styles.userPhoto} defaultSource={IconImages.ic_user} />
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    overflow: 'hidden',
  },
  messageContainer: {
    flex: 1,
  },
  formContainer: {
    flexDirection: 'row',
    backgroundColor: AppConst.chatColor,
    justifyContent: 'space-around',
    alignItems: 'center',
  },
  imageContainer: {
    backgroundColor: AppConst.chatColor,
    alignItems: 'center',
  },
  messageInputBase: {
    width: "70%",
    minHeight: 25,
    maxHeight: 90,
    margin: 2,
  },
  messageInput: {
    borderRadius: 5,
    backgroundColor: '#ffffff',
    paddingLeft: 10,
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
  },
  messageIcon: {
    width: 30,
    height: 30,
  },
  pictureIcon: {
    marginTop: 10,
    width: 60,
    height: 60,
  },
  iconText: {
    color: '#ffffff',
  },

  /** メッセージ用 */
  chatText: {
    color: '#000000',
    fontSize: 11,
  },
  dayLabelArea: {
    alignItems: "center",
  },
  dayLabel: {
    backgroundColor: "#888888",
    color: "#ffffff",
    borderRadius: 5,
    paddingHorizontal: 5,
    fontSize: 11,
  },
  fromMe: {
    backgroundColor: '#ffffff',
    borderColor: '#70ad47',
    padding: 10,
    marginLeft: '15%',
    marginTop: 5,
    marginRight: '5%',
    marginBottom: 5,
    maxWidth: '80%',
    alignSelf: 'flex-end',
    borderRadius: 25,
  },
  fromThemBox: {
    flexDirection: 'row',
    padding: 10,
    marginTop: 5,
    marginRight: '15%',
    marginBottom: 5,
  },
  fromThem: {
    backgroundColor: '#ffffff',
    borderColor: '#70ad47',
    padding: 10,
    marginLeft: '5%',
    marginTop: 5,
    marginRight: '5%',
    marginBottom: 5,
    maxWidth: '80%',
    alignSelf: 'flex-start',
    borderRadius: 25,
  },
  userPhoto: {
    borderRadius: 15,
    width: 30,
    height: 30,
    backgroundColor: 'gray',
    zIndex: 1000,
  },
  chatPhoto: {
    width: 100,
    height: 100
  },
  fromMePhoto: {
    padding: 10,
    marginLeft: '45%',
    marginTop: 5,
    marginRight: '5%',
    marginBottom: 5,
    maxWidth: '50%',
    alignSelf: 'flex-end',
  },
  fromThemPhoto: {
    padding: 10,
    marginLeft: '5%',
    marginTop: 5,
    marginRight: '45%',
    marginBottom: 5,
    maxWidth: '50%',
    alignSelf: 'flex-start',
  },
  rightArrow: {
    position: 'absolute',
    backgroundColor: '#ffffff',
    width: 20,
    height: 20,
    right: -10,
    bottom: 0,
    borderBottomLeftRadius: 16,
  },
  rightArrowOverlap: {
    position: 'absolute',
    backgroundColor: AppConst.defaultBaseColor,
    width: 26,
    height: 20,
    right: -26,
    bottom: 0,
    borderBottomLeftRadius: 10,
  },
  leftArrow: {
    position: 'absolute',
    backgroundColor: '#ffffff',
    width: 20,
    height: 20,
    left: -10,
    bottom: 0,
    borderBottomRightRadius: 16,
  },
  leftArrowOverlap: {
    position: 'absolute',
    backgroundColor: AppConst.defaultBaseColor,
    width: 26,
    height: 20,
    left: -26,
    bottom: 0,
    borderBottomRightRadius: 10,
  },
});

export default ChatMain;
